/**
 * @voltxampere/engine — pnjlim (Nagel 1975) junction voltage limiter
 *
 * Nagel's original SPICE thesis (*SPICE 2: A Computer Program to
 * Simulate Semiconductor Circuits*, Appendix D, 1975) introduced
 * the standard algorithm every modern SPICE derivative uses to keep
 * Newton-Raphson iterates from overshooting a p-n junction's
 * exponential. The mathematics:
 *
 *   Vcrit = Vt · ln(Vt / (√2 · Is))
 *
 * is the threshold at which `exp(Vd / Vt)` grows fast enough that an
 * unconstrained Newton step would double-count its own slope and
 * blow up on the next iterate.
 *
 * The limiter {@link pnjlim} compares a candidate voltage `vnew`
 * (what the current Newton iterate says the junction is) against the
 * previous iterate's limited voltage `vold`. If the step is both
 * past `Vcrit` AND larger than `2·Vt` in magnitude, we apply the
 * Nagel clamp:
 *
 *   * `vold > 0`: relax toward `vold + Vt·ln(1 + (vnew - vold)/Vt)`
 *     when the log argument stays positive, else freeze at `Vcrit`.
 *   * `vold ≤ 0`: fresh ascent from near-zero — use `Vt·ln(vnew/Vt)`
 *     which is smooth and bounded.
 *
 * Small steps pass through unchanged. This keeps the limiter
 * transparent in the stable region (Newton converges quadratically
 * near the operating point) and only engages when a genuinely large
 * step would poison the next iterate.
 *
 * Sprint 5 shipped a cheaper "cap at Vcrit" damping inside the diode
 * stamp. Sprint 7 promotes that to proper pnjlim and closes the
 * Sprint 5 D3 deferred item.
 *
 * The companion {@link computeVcrit} derives Vcrit from `(Vt, Is)`
 * per the formula above — kept here so stamps never hand-roll the
 * expression (one small difference in sign convention has been the
 * source of multiple SPICE-to-SPICE divergence bugs historically).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Compute the critical voltage for a p-n junction with saturation
 * current `Is` at thermal voltage `Vt`. Returns `Vt · ln(Vt / (√2 · Is))`.
 *
 * @throws {ContractViolationError} when `Vt` or `Is` are not
 *   positive finite numbers. A negative/zero Is makes no physical
 *   sense (no junction current at all), and a zero Vt would divide
 *   by zero inside `ln`.
 */
export function computeVcrit(vt, Is) {
    if (!Number.isFinite(vt) || vt <= 0) {
        throw new ContractViolationError(`computeVcrit: vt must be a positive finite number, got ${String(vt)}`);
    }
    if (!Number.isFinite(Is) || Is <= 0) {
        throw new ContractViolationError(`computeVcrit: Is must be a positive finite number, got ${String(Is)}`);
    }
    return vt * Math.log(vt / (Math.SQRT2 * Is));
}
/**
 * Apply Nagel's pnjlim junction voltage limiter.
 *
 * @param vnew Candidate junction voltage (current Newton iterate).
 * @param vold Previous iteration's limited voltage (0 at cold start).
 * @param vt Thermal voltage (`Vt = kT/q`, ≈ 0.02585 V at 27 °C).
 * @param vcrit Critical voltage, typically {@link computeVcrit}.
 * @param enableSymmetricClamp Sprint 8a.1 master toggle. `true`
 *   (default) enables the Sprint 8a symmetric downward clamp for
 *   conducting junctions. `false` reverts to pure Nagel 1975
 *   upward-only limiting — use when the caller disabled Sprint 8a
 *   damping end-to-end (see `DCSettings.enableDeltaVDamping` in
 *   `@voltxampere/schema`).
 * @returns A voltage `≤ vnew` that keeps `exp(vLim / Vt)` finite
 *          and monotone across Newton iterations.
 */
export function pnjlim(vnew, vold, vt, vcrit, enableSymmetricClamp = true) {
    // Sprint 8a: symmetric downward clamp for conducting junctions.
    // Sprint 8a.1 tightened the engagement threshold to `vcrit - 2·vt`
    // (was `vold > 0`). Original rationale was "BJT activity begins at
    // ~0.5 V", but Agent 3's Sprint 8a review measured the clamp as
    // empirically **dormant** on the PNP CE orbit — V_EB there never
    // drops below ~0.16 V. Meanwhile the loose `vold > 0` threshold
    // risked spurious fire during cold-start diode iterations where
    // small thermal-noise-scale voltages could trigger clamping. The
    // tightened threshold engages only when the junction is **already
    // close to conducting** (vold within 2·vt of vcrit), which is the
    // regime where a Newton downward overshoot actually causes
    // reverse-bias collapse (the mechanism the clamp was meant to
    // protect).
    //
    // The `enableSymmetricClamp` flag (Sprint 8a.1 hybrid-mode fix)
    // short-circuits the clamp when the caller opted out of Sprint 8a
    // numerics — `DCSettings.enableDeltaVDamping: false` must produce
    // pre-Sprint-8a behaviour end-to-end, not a hybrid with the
    // node-level damping off and the junction-level clamp still live.
    if (enableSymmetricClamp && vold > vcrit - 2 * vt && vnew < vold - 2 * vt) {
        return vold - 2 * vt;
    }
    // Short-circuit the stable regime. Newton converges quadratically
    // near the operating point, so the large majority of iterations
    // pass through with zero change.
    if (vnew <= vcrit)
        return vnew;
    if (Math.abs(vnew - vold) <= 2 * vt)
        return vnew;
    if (vold > 0) {
        const arg = 1 + (vnew - vold) / vt;
        if (arg > 0) {
            return vold + vt * Math.log(arg);
        }
        // Log argument went non-positive — fall back to Vcrit as a
        // safe ceiling (Newton will walk back to the operating point
        // across the next few iterations).
        return vcrit;
    }
    // vold ≤ 0 branch: ascending from a cold / reverse-biased start.
    // Use `Vt·ln(vnew/Vt)` which is smooth and bounded even for
    // pathologically large vnew.
    return vt * Math.log(vnew / vt);
}
//# sourceMappingURL=pnjlim.js.map